import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { NotificationPolicyService } from "../../services/notification.policy.service";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { NotificationPolicyMapper } from "./notification.policy.mapper";
import { NotificationPolicyModel } from "./notification.policy.model";
import { S25NotificationPolicyComponent } from "./s25.notification.policy.component";
import { ObjectType } from "../s25-event/ReservationI";

function toBoolInput(value: string | boolean | undefined, defaultValue: boolean): boolean {
    if (value === undefined || value === null) {
        return defaultValue;
    }
    return S25Util.toBool(value);
}

@TypeManagerDecorator("s25-ng-object-notify-policy")
@Component({
    selector: "s25-ng-object-notify-policy",
    template: `
        <s25-ng-notification-policy
            [readOnly]="readOnly()"
            [showRemove]="false"
            [fetchItemTypeId]="itemTypeId()"
            [fetchItemId]="itemId()"
        />
        @if (showActions() && allowEdit() && policyReady()) {
            <div class="notification-policy__actions c-margin-bottom--half c-margin-left--half">
                @if (policyEditor()?.dirty()) {
                    <button type="button" class="aw-button aw-button--primary c-margin-right--quarter" (click)="save()">
                        Save
                    </button>
                    <button
                        type="button"
                        class="aw-button aw-button--outline c-margin-right--quarter"
                        (click)="refresh()"
                    >
                        Cancel
                    </button>
                }
                @if (policyEditor()?.hasPolicyContent()) {
                    <button type="button" class="aw-button aw-button--danger--outline" (click)="removePolicy()">
                        Remove notification policy
                    </button>
                }
                <s25-ng-fading-success-msg [(message)]="saveSuccessMessage" />
            </div>
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    styles: `
        .notification-policy__actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        }
    `,
})
export class S25ObjectNotifyPolicyHostComponent {
    readonly itemId = input<number | undefined>(undefined);
    readonly type = input<ObjectType | undefined>(undefined);
    readonly canEdit = input(true, { transform: (value: string | boolean) => toBoolInput(value, true) });
    readonly editModeOff = input(false, { transform: (value: string | boolean) => toBoolInput(value, false) });
    readonly showActions = input(true, { transform: (value: string | boolean) => toBoolInput(value, true) });

    readonly policyEditor = viewChild(S25NotificationPolicyComponent);
    readonly saveSuccessMessage = signal("");
    readonly saving = signal(false);
    readonly notifyObjectType = computed(() => NotificationPolicyModel.resolveObjectType(this.type()));
    readonly itemTypeId = computed(() => {
        const objectType = this.notifyObjectType();
        if (!objectType) {
            return undefined;
        }
        return NotificationPolicyModel.notifyItemTypeIdFromObjectType(objectType);
    });
    readonly allowEdit = computed(() => this.canEdit() && !this.editModeOff());
    readonly readOnly = computed(() => !this.allowEdit());
    readonly policyReady = computed(() => {
        const editor = this.policyEditor();
        return !!editor && !editor.isLoading();
    });

    constructor() {
        inject(ElementRef).nativeElement.angBridge = this;
    }

    removePolicy() {
        const editor = this.policyEditor();
        if (!editor) {
            return;
        }
        const shouldPersistRemoval = !!this.itemId() && !editor.getSession().isNewPolicy;
        editor.markPolicyRemoved();
        if (shouldPersistRemoval) {
            void this.delete();
        }
    }

    delete() {
        const itemTypeId = this.itemTypeId();
        const itemId = this.itemId();
        if (itemTypeId == null || itemId == null) {
            return Promise.resolve();
        }
        this.saving.set(true);
        return NotificationPolicyService.delete(itemTypeId, itemId).then(
            (resp) => {
                this.saving.set(false);
                this.refresh();
                if (this.showActions()) {
                    this.saveSuccessMessage.set("Removed.");
                }
                return resp;
            },
            (caughtError) => {
                this.saving.set(false);
                S25Util.showError(caughtError);
            },
        );
    }

    save() {
        const editor = this.policyEditor();
        if (!editor) {
            return Promise.resolve();
        }
        const draft = editor.getSession().draft;
        if (NotificationPolicyMapper.isRemovedPolicy(draft)) {
            return this.delete();
        }
        const itemTypeId = this.itemTypeId();
        const itemId = this.itemId();
        if (itemTypeId == null || itemId == null) {
            return Promise.resolve();
        }
        if (!editor.validate()) {
            return Promise.reject({ error: editor.validationMessage() });
        }
        this.saving.set(true);
        return NotificationPolicyService.saveObjectNotify(editor.getSession(), itemTypeId, itemId).then(
            (resp) => {
                editor.commitBaseline();
                editor.validationMessage.set("");
                this.saveSuccessMessage.set("Saved.");
                this.saving.set(false);
                return resp;
            },
            (caughtError) => {
                this.saving.set(false);
                S25Util.showError(caughtError);
            },
        );
    }

    refresh() {
        this.saveSuccessMessage.set("");
        this.policyEditor()?.reset();
    }
}
